import { StyledModalDialog } from "@src/common/styled-components";
import { LAYOUT_CONSTANTS } from "@src/constants";
import { useIdleTimerContext } from "@src/context/IdleTimerContext";
import { isRegisteredEntryRoutePath } from "@src/routes/entry-route-registry";
import { useLocation } from "@uhg-abyss/web/tools/reactRouterTools";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Text } from "@uhg-abyss/web/ui/Text";
import type { CSSProperties, ReactNode } from "react";
import { Fragment } from "react";

// ─── Shared styles ────────────────────────────────────────────────
const footerStyle: CSSProperties = {
  display: "flex",
  gap: "12px",
  justifyContent: "flex-start",
  width: "100%",
  paddingTop: "16px",
};

const textStyle = { marginBottom: "12px" };
// Stryker restore ObjectLiteral,StringLiteral

// ─── Button shape ─────────────────────────────────────────────────
interface ModalButton {
  label: ReactNode;
  onClick: () => void;
  variant?: "filled" | "outline";
  testId: string;
  id: string;
  obcLabel: string;
}

// ─── Base modal ───────────────────────────────────────────────────
interface BaseIdleModalProps {
  testId: string;
  id: string;
  obcLabel: string;
  header: string;
  onClose: () => void;
  buttons: ModalButton[];
  children: ReactNode;
}

const BaseIdleModal = ({
  testId,
  id,
  obcLabel,
  header,
  onClose,
  buttons,
  children,
}: BaseIdleModalProps) => {
  return (
    <StyledModalDialog
      data-testid={testId}
      data-id={id}
      data-obc-label={obcLabel}
      header={header}
      isOpen
      onClose={onClose}
      closeOnClickOutside={false}
      size={LAYOUT_CONSTANTS.modalDialogWidth}
      footer={
        <div style={footerStyle}>
          {buttons.map((btn) => {
            return (
              <Button
                key={btn.id}
                data-testid={btn.testId}
                id={btn.id}
                data-obc-label={btn.obcLabel}
                onClick={btn.onClick}
                variant={btn.variant ?? "filled"}
              >
                {btn.label}
              </Button>
            );
          })}
        </div>
      }
    >
      {children}
    </StyledModalDialog>
  );
};

// ─── Main export ──────────────────────────────────────────────────
export const IdleTimeoutModal = () => {
  const { pathname } = useLocation();
  const {
    modalState,
    onStayInSession,
    onEndSession,
    onReReserve,
    onDismissAndEndSession,
    onAcknowledgeTimeout,
    hasAppointmentHold,
    isIdleTimerDisabled,
  } = useIdleTimerContext();

  const isFlowStartRoute = isRegisteredEntryRoutePath(pathname);

  // Stryker disable next-line ConditionalExpression,StringLiteral
  if (modalState === "active") return null;

  // Timeout modal should only be shown after redirecting to the beginning of flow.
  if (modalState === "timeout" && !isFlowStartRoute) return null;

  // Keep existing idle disable behavior for non-timeout modals.
  if (modalState !== "timeout" && isIdleTimerDisabled) return null;

  // ─── Warning: "Still here?" ───────────────────────────────────
  if (modalState === "warning") {
    return (
      <BaseIdleModal
        testId="idle-timeout-warning-modal"
        id="obc-idle-timeout-warning-modal"
        obcLabel="Idle Timeout Warning Modal"
        header="Still here?"
        onClose={onEndSession}
        buttons={[
          {
            label: "I\u2019m here",
            onClick: onStayInSession,
            testId: "idle-modal-im-here-btn",
            id: "obc-idle-modal-im-here-btn",
            obcLabel: "Idle Modal I'm Here Button",
          },
          {
            label: "End session",
            onClick: onEndSession,
            variant: "outline",
            testId: "idle-modal-end-session-btn",
            id: "obc-idle-modal-end-session-btn",
            obcLabel: "Idle Modal End Session Button",
          },
        ]}
      >
        {hasAppointmentHold ? (
          <Fragment>
            <Text css={textStyle}>
              We noticed you&rsquo;ve been away for a bit. To keep your session secure, your
              appointment hold has expired. We&rsquo;ve saved everything you entered &mdash; when
              you&rsquo;re ready, click <strong>I&rsquo;m here</strong> to re&#8209;reserve your
              appointment and continue.
            </Text>
            <Text css={textStyle}>
              If you close your browser or choose <strong>End session</strong>, your entered
              information will be cleared and you&rsquo;ll need to start over.
            </Text>
          </Fragment>
        ) : (
          <Fragment>
            <Text css={textStyle}>
              We noticed you&rsquo;ve been away for a bit. To keep your session secure, we&rsquo;ll
              need to clear your selections if you remain inactive. Click{" "}
              <strong>I&rsquo;m here</strong> to continue where you left off.
            </Text>
            <Text css={textStyle}>
              If you close your browser or choose <strong>End session</strong>, your selections will
              be cleared and you&rsquo;ll need to start over.
            </Text>
          </Fragment>
        )}
      </BaseIdleModal>
    );
  }

  // ─── Confirmation: "Thanks — your info is saved" ──────────────
  if (modalState === "confirmation") {
    return (
      <BaseIdleModal
        testId="idle-timeout-confirmation-modal"
        id="obc-idle-timeout-confirmation-modal"
        obcLabel="Idle Timeout Confirmation Modal"
        header="Thanks — your info is saved"
        onClose={onDismissAndEndSession}
        buttons={[
          {
            label: "Select appointment again",
            onClick: onReReserve,
            testId: "idle-modal-re-reserve-btn",
            id: "obc-idle-modal-re-reserve-btn",
            obcLabel: "Idle Modal Re-reserve Appointment Button",
          },
          {
            label: "Later / Close",
            onClick: onDismissAndEndSession,
            variant: "outline",
            testId: "idle-modal-later-close-btn",
            id: "obc-idle-modal-later-close-btn",
            obcLabel: "Idle Modal Later Close Button",
          },
        ]}
      >
        <Text css={textStyle}>
          We saved your answers so you won&rsquo;t need to re&#8209;enter them. To keep your
          appointment slot, please re&#8209;reserve now.
        </Text>
        <Text>
          If you close your browser or end the session before re&#8209;reserving, your saved answers
          will be cleared and you&rsquo;ll need to start again.
        </Text>
      </BaseIdleModal>
    );
  }

  // ─── Timeout: "Your session has expired" ─────────────────────
  // Stryker disable next-line ConditionalExpression
  if (modalState === "timeout") {
    return (
      <BaseIdleModal
        testId="idle-timeout-expired-modal"
        id="obc-idle-timeout-expired-modal"
        obcLabel="Idle Timeout Expired Modal"
        header="Your session has expired"
        onClose={onAcknowledgeTimeout}
        buttons={[
          {
            label: "Continue",
            onClick: onAcknowledgeTimeout,
            testId: "idle-modal-timeout-continue-btn",
            id: "obc-idle-modal-timeout-continue-btn",
            obcLabel: "Idle Modal Timeout Continue Button",
          },
        ]}
      >
        <Text css={textStyle}>
          For your security, your session has expired due to inactivity. To protect your personal
          information, we&rsquo;ve securely cleared your session data.
        </Text>
        <Text>
          Click <strong>Continue</strong> to return to the beginning and start a new session.
        </Text>
      </BaseIdleModal>
    );
  }

  return null;
};
